// src/pages/DashboardPage.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
  onSnapshot,
  doc,
  getDoc, // ✅ ensure getDoc is imported
} from 'firebase/firestore';
import { intervalToDuration } from 'date-fns';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import TaskList from '../components/TaskList';
import styles from '../styles/DashboardPage.module.css';
import EventPopupModal from '../components/EventPopupModal';
import LocationPermission from '../pages/LocationPermission';
import AttendanceCard from '../components/AttendanceCard';
import MissedCheckoutAlert from '../components/MissedCheckoutAlert';
import AnnouncementModal from '../components/AnnouncementModal';
import PushNotificationPrompt from '../components/PushNotificationPrompt';
import { useSocialLanguage } from '../hooks/useSocialLanguage';

// --- Bilingual (AR/EN) display strings ---
const T = {
  ar: {
    errLoginRequired: 'تسجيل الدخول مطلوب لعرض المهام.',
    errIndexMissing: 'فهرس قاعدة البيانات مفقود. يرجى التحقق من إعدادات فهرس Firestore.',
    errPermissionDenied: 'تم رفض الإذن لعرض المهام.',
    errLoadTasks: 'فشل تحميل المهام في الوقت الفعلي. يرجى المحاولة لاحقًا.',
    errLoadEmployees: 'فشل تحميل قائمة الموظفين.',
    errLoadEvents: 'فشل تحميل فعاليات اليوم.',
    vacationPre: 'أنت مُسجَّل في',
    vacationWord: 'إجازة',
    vacationPost: 'اليوم. السجل اليومي والحضور غير مطلوبين.',
    todaysEvents: 'فعاليات اليوم',
    loadingEvents: 'جارٍ تحميل الفعاليات…',
    popupIndicator: ' (منبثق)',
    noEvents: 'لا توجد فعاليات مجدولة لليوم.',
    birthday: (name) => `🎂 عيد ميلاد سعيد، ${name}! 🎉`,
    quickStats: 'إحصائياتك السريعة',
    forLabel: 'الفترة:',
    optToday: 'اليوم',
    optYesterday: 'أمس',
    optLast7: 'آخر 7 أيام',
    optLast28: 'آخر 28 يومًا',
    optCustom: 'نطاق مخصص',
    optAllTime: 'كل الأوقات',
    ariaCustomStart: 'تاريخ البدء المخصص للإحصائيات',
    ariaCustomEnd: 'تاريخ النهاية المخصص للإحصائيات',
    toWord: 'إلى',
    cardCreated: 'المهام المنشأة',
    cardCurrentlyAssigned: 'المُسندة حاليًا',
    cardAssignedCompleted: 'المُسندة والمكتملة',
    cardAssignedLate: 'المُسندة والمتأخرة',
    cardAvgAck: 'متوسط وقت الإقرار',
    cardAvgCompletion: 'متوسط وقت الإنجاز',
    fromAck: '(من الإقرار)',
    tasksWord: 'مهمة',
    cardCompanyApprovals: 'موافقات الشركة المنجَزة',
    cardCustomerApprovals: 'موافقات العميل المنجَزة',
    customDateInfo: 'يرجى تحديد تاريخ بدء وتاريخ نهاية للنطاق المخصص.',
    customDateError: 'يجب أن يكون تاريخ النهاية بعد تاريخ البدء للنطاق المخصص.',
    titleMyDashboard: 'لوحتي',
    titleAllTasks: 'كل المهام',
    titleMyTasks: 'مهامي',
    addEvent: 'إضافة فعالية',
    createTask: 'إنشاء مهمة',
    viewMyTraineeTasks: 'عرض مهام التدريب الخاصة بي',
    statusFilterLabel: 'الحالة:',
    allActiveTasks: 'كل المهام النشطة',
    showCompleted: 'عرض المكتملة:',
    completedToday: 'المكتملة اليوم',
    allTime: 'كل الأوقات',
    showCancelled: 'عرض الملغاة:',
    cancelledToday: 'الملغاة اليوم',
    assigneeLabel: 'المسند إليه:',
    allAssignees: 'كل المسند إليهم',
    clearFilters: 'مسح الفلاتر والترتيب',
    loadingTasks: 'جارٍ تحميل المهام…',
    noTasks: 'لا توجد مهام مطابقة للفلاتر الحالية',
    traineeInfoPre: 'تتم إدارة مهامك في ',
    traineeDashLink: 'لوحة مهام التدريب',
    traineeInfoPost: '.',
  },
  en: {
    errLoginRequired: 'Login required to view tasks.',
    errIndexMissing: 'Database index missing. Please check Firestore index configuration.',
    errPermissionDenied: 'Permission denied to view tasks.',
    errLoadTasks: 'Failed to load tasks in real-time. Please try again later.',
    errLoadEmployees: 'Failed to load employee list.',
    errLoadEvents: "Failed to load today's events.",
    vacationPre: 'You’re marked on',
    vacationWord: 'Vacation',
    vacationPost: 'today. Daily log and attendance are not required.',
    todaysEvents: "Today's Events",
    loadingEvents: 'Loading events...',
    popupIndicator: ' (Pop-up)',
    noEvents: 'No events scheduled for today.',
    birthday: (name) => `🎂 Happy Birthday, ${name}! 🎉`,
    quickStats: 'Your Quick Stats',
    forLabel: 'For:',
    optToday: 'Today',
    optYesterday: 'Yesterday',
    optLast7: 'Last 7 Days',
    optLast28: 'Last 28 Days',
    optCustom: 'Custom Range',
    optAllTime: 'All Time',
    ariaCustomStart: 'Custom start date for stats',
    ariaCustomEnd: 'Custom end date for stats',
    toWord: 'to',
    cardCreated: 'Tasks Created',
    cardCurrentlyAssigned: 'Currently Assigned',
    cardAssignedCompleted: 'Assigned & Completed',
    cardAssignedLate: 'Assigned & Late',
    cardAvgAck: 'Avg. Ack. Time',
    cardAvgCompletion: 'Avg. Completion Time',
    fromAck: '(from Ack)',
    tasksWord: 'tasks',
    cardCompanyApprovals: 'Company Approvals Done',
    cardCustomerApprovals: 'Customer Approvals Done',
    customDateInfo: 'Please select both a start and end date for the custom range.',
    customDateError: 'End date must be after start date for custom range.',
    titleMyDashboard: 'My Dashboard',
    titleAllTasks: 'All Tasks',
    titleMyTasks: 'My Tasks',
    addEvent: 'Add Event',
    createTask: 'Create Task',
    viewMyTraineeTasks: 'View My Trainee Tasks',
    statusFilterLabel: 'Status:',
    allActiveTasks: 'All Active Tasks',
    showCompleted: 'Show Completed:',
    completedToday: 'Completed Today',
    allTime: 'All Time',
    showCancelled: 'Show Cancelled:',
    cancelledToday: 'Cancelled Today',
    assigneeLabel: 'Assignee:',
    allAssignees: 'All Assignees',
    clearFilters: 'Clear Filters & Sort',
    loadingTasks: 'Loading tasks...',
    noTasks: 'No tasks match the current filters.',
    traineeInfoPre: 'Your tasks are managed on the ',
    traineeDashLink: 'Trainee Tasks Dashboard',
    traineeInfoPost: '.',
  },
};

// Display-only label for a stored (English) status value.
const statusLabel = (s, lang) =>
  lang === 'ar'
    ? ({
        'Not Started': 'لم تبدأ',
        'In Progress': 'قيد التنفيذ',
        'Company Approval': 'موافقة الشركة',
        'Customer Approval': 'موافقة العميل',
        'Completed': 'مكتملة',
        'Cancelled': 'ملغاة',
      }[s] || s)
    : s;

// --- Helper Function Definitions ---

const parseDateSafely = (dateValue) => {
  if (!dateValue) return null;
  try {
    if (dateValue instanceof Timestamp) {
      return dateValue.toDate();
    } else if (typeof dateValue === 'string') {
      const dateString =
        dateValue.length === 10 && !dateValue.includes('T')
          ? dateValue + 'T00:00:00Z'
          : dateValue;
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        const parts = dateValue.split('-');
        if (parts.length === 3) {
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const day = parseInt(parts[2], 10);
          const utcDate = new Date(Date.UTC(year, month, day));
          if (!isNaN(utcDate.getTime())) return utcDate;
        }
        console.warn('Invalid date string after attempting parse:', dateValue);
        return null;
      }
      return date;
    } else if (dateValue instanceof Date && !isNaN(dateValue)) {
      return dateValue;
    }
    console.warn('Unknown or invalid date format provided to parseDateSafely:', dateValue);
    return null;
  } catch (error) {
    console.error('Error parsing date in parseDateSafely:', error, dateValue);
    return null;
  }
};

const getPriorityValue = (priority) => {
  const order = { high: 1, medium: 2, low: 3 };
  return order[priority?.toLowerCase()] || 99;
};

const isTaskOverdue = (task) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = parseDateSafely(task.endDate);
  const statusLower = task.status?.toLowerCase();
  if (!endDate || statusLower === 'completed' || statusLower === 'cancelled' || endDate >= today) return false;
  return true;
};

const getTodayYYYYMMDD = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getStartOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const isDateInRange = (dateFieldValue, rangeStart, rangeEnd) => {
  if (rangeStart === null && rangeEnd === null) return !!dateFieldValue;
  if (!dateFieldValue) return false;

  const dateToTest = parseDateSafely(dateFieldValue);
  if (!dateToTest) return false;

  let inRange = true;
  if (rangeStart && dateToTest < rangeStart) inRange = false;
  if (rangeEnd && dateToTest > rangeEnd) inRange = false;
  return inRange;
};

const formatAverageDuration = (averageMilliseconds, lang = 'en') => {
  const tr = (en, ar) => (lang === 'ar' ? ar : en);
  const u = { d: tr('d', 'ي'), h: tr('h', 'س'), m: tr('m', 'د'), s: tr('s', 'ث') };
  if (averageMilliseconds === null || averageMilliseconds < 0 || isNaN(averageMilliseconds)) return tr('N/A', 'غير متاح');
  if (averageMilliseconds === 0) return tr('Instantly', 'فورًا');

  const endMs = Math.max(0, averageMilliseconds);
  const duration = intervalToDuration({ start: 0, end: endMs });

  const parts = [];
  if (duration.days && duration.days > 0) parts.push(`${duration.days}${u.d}`);
  if (duration.hours && duration.hours > 0) parts.push(`${duration.hours}${u.h}`);

  if (parts.length < 2 && duration.minutes && duration.minutes > 0) parts.push(`${duration.minutes}${u.m}`);

  if (parts.length === 0) {
    if (duration.seconds && duration.seconds > 0) parts.push(`${duration.seconds}${u.s}`);
    else if (averageMilliseconds > 0) return tr('< 1s', `< 1${u.s}`);
    else return `0${u.s}`;
  }
  if (parts.length === 0 && averageMilliseconds > 0) return tr('< 1m', `< 1${u.m}`);
  if (parts.length === 0) return tr('N/A', 'غير متاح');
  return parts.slice(0, 2).join(' ');
};

// NEW: Check if the current user is on vacation today (index-friendly with safe fallback)
const isOnVacationToday = async (uid) => {
  if (!uid) return false;
  const today = getTodayYYYYMMDD();

  try {
    // Preferred: indexed query userId + date
    const q1 = query(
      collection(db, 'attendance'),
      where('userId', '==', uid),
      where('date', '==', today)
    );
    const snap = await getDocs(q1);
    let onVac = false;
    snap.forEach((d) => {
      const data = d.data() || {};
      const status = (data.status || '').toLowerCase();
      if (status === 'vacation' || data?.vacation?.type || data?.vacation?.isVacation) {
        onVac = true;
      }
    });
    return onVac;
  } catch (err) {
    console.warn('Vacation check (userId+date) needs index or failed; using fallback:', err);
    try {
      // Fallback: query just by userId and filter by date client-side
      const q2 = query(collection(db, 'attendance'), where('userId', '==', uid));
      const snap2 = await getDocs(q2);
      let onVac = false;
      snap2.forEach((d) => {
        const data = d.data() || {};
        if (data.date === today) {
          const status = (data.status || '').toLowerCase();
          if (status === 'vacation' || data?.vacation?.type || data?.vacation?.isVacation) {
            onVac = true;
          }
        }
      });
      return onVac;
    } catch (err2) {
      console.warn('Vacation fallback query failed:', err2);
      return false; // default to not on vacation if uncertain
    }
  }
};

// --- End Helper Function Definitions ---

const DashboardPage = () => {
  const { currentUser, userRole } = useAuth();
  const { language } = useSocialLanguage();
  const t = T[language] || T.ar;

  // --- Vacation-aware UI state ---
  const [onVacationTodayFlag, setOnVacationTodayFlag] = useState(false);
  const [checkingVacation, setCheckingVacation] = useState(true);

  // --- Component State ---
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customEvents, setCustomEvents] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedAssignee, setSelectedAssignee] = useState('all');
  const [completedFilter, setCompletedFilter] = useState('today');
  const [cancelledFilter, setCancelledFilter] = useState('today');

  // Sorting
  const [sortField, setSortField] = useState('endDate');
  const [sortDirection, setSortDirection] = useState('asc');

  const [isEventPopupOpen, setIsEventPopupOpen] = useState(false);

  // Achievements date filter
  const [achievementsDateFilter, setAchievementsDateFilter] = useState('today');
  const [customAchievementStartDate, setCustomAchievementStartDate] = useState('');
  const [customAchievementEndDate, setCustomAchievementEndDate] = useState('');

  const isLoading = loadingTasks || loadingEvents || loadingEmployees;

  // ✅ NEW: location permission session flag
  const [locationPermissionHandled, setLocationPermissionHandled] = useState(
    !!sessionStorage.getItem('locationPermissionHandled')
  );
  const handlePermissionResult = (success, message) => {
    sessionStorage.setItem('locationPermissionHandled', 'true');
    setLocationPermissionHandled(true);
    console.log(message);
  };

  // ✅ NEW: company location state for AttendanceCard
  const [companyLocation, setCompanyLocation] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(true);

  // --- Effects: Vacation check (controls daily-log prompting UX pieces here) ---
  useEffect(() => {
    let ignore = false;
    const go = async () => {
      if (!currentUser?.uid) {
        if (!ignore) {
          setOnVacationTodayFlag(false);
          setCheckingVacation(false);
        }
        return;
      }
      try {
        const onVac = await isOnVacationToday(currentUser.uid);
        if (!ignore) setOnVacationTodayFlag(onVac);
      } finally {
        if (!ignore) setCheckingVacation(false);
      }
    };
    go();
    return () => {
      ignore = true;
    };
  }, [currentUser]);

  // --- Effects: Tasks ---
  useEffect(() => {
    if (!currentUser) {
      if (currentUser === null) {
        setLoadingTasks(false);
        setError(t.errLoginRequired);
        setTasks([]);
      } else {
        setLoadingTasks(true);
      }
      return;
    }
    if (!userRole) {
      setLoadingTasks(true);
      return;
    }
    if (userRole === 'Trainee') {
      setTasks([]);
      setLoadingTasks(false);
      setError('');
      return;
    }

    setLoadingTasks(true);
    setError('');

    const tasksCol = collection(db, 'tasks');
    let qy;
    if (userRole === 'Manager' || userRole === 'Accounts Manager') {
      qy = query(tasksCol);
    } else {
      qy = query(tasksCol, where('visibleTo', 'array-contains', currentUser.uid));
    }

    const unsubscribe = onSnapshot(
      qy,
      (querySnapshot) => {
        const fetchedTasksRaw = querySnapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            taskSystem: data.taskSystem || null,
            startDate: parseDateSafely(data.startDate),
            endDate: parseDateSafely(data.endDate),
            createdAt: parseDateSafely(data.createdAt),
            updatedAt: parseDateSafely(data.updatedAt),
            completedAt: parseDateSafely(data.completedAt),
            cancelledAt: parseDateSafely(data.cancelledAt),
            acknowledgedBy: data.acknowledgedBy || {},
          };
        });

        const nonTraineeSystemTasks = fetchedTasksRaw.filter((task) => task.taskSystem !== 'trainee');
        setTasks(nonTraineeSystemTasks);
        setLoadingTasks(false);
        setError('');
      },
      (err) => {
        console.error('Error listening to task updates:', err);
        if (err.code === 'failed-precondition') {
          setError(t.errIndexMissing);
        } else if (err.code === 'permission-denied') {
          setError(t.errPermissionDenied);
        } else {
          setError(t.errLoadTasks);
        }
        setTasks([]);
        setLoadingTasks(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, userRole]);

  // --- Effects: Employees ---
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const usersCol = collection(db, 'users');
        const q_users = query(usersCol, orderBy('name', 'asc'));
        const querySnapshot = await getDocs(q_users);
        const employeeList = querySnapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            name: data.name || data.email,
            dateOfBirth: data.dateOfBirth || null,
            role: data.role || 'Unknown',
          };
        });
        setEmployees(employeeList);
      } catch (err) {
        console.error('Error fetching employees:', err);
        setError((prev) => (prev ? `${prev} | ${t.errLoadEmployees}` : t.errLoadEmployees));
      } finally {
        setLoadingEmployees(false);
      }
    };
    if (currentUser) {
      fetchEmployees();
    } else if (!loadingTasks && !currentUser) {
      setLoadingEmployees(false);
    }
  }, [currentUser, loadingTasks]);

  // --- Effects: Today's Events ---
  useEffect(() => {
    const fetchTodaysEvents = async () => {
      setLoadingEvents(true);
      const todayStr = getTodayYYYYMMDD();
      try {
        const eventsCol = collection(db, 'events');
        const q_events = query(eventsCol, where('date', '==', todayStr));
        const eventSnapshot = await getDocs(q_events);
        const fetchedEvents = eventSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setCustomEvents(fetchedEvents);
      } catch (err) {
        console.error("Error fetching custom events:", err);
        setError((prev) => (prev ? `${prev} | ${t.errLoadEvents}` : t.errLoadEvents));
        setCustomEvents([]);
      } finally {
        setLoadingEvents(false);
      }
    };
    fetchTodaysEvents();
  }, []);

  // ✅ NEW: Effect to fetch company location for AttendanceCard
  useEffect(() => {
    const fetchCompanyLocation = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'companyProfile');
        const docSnap = await getDoc(settingsRef);
        if (docSnap.exists() && docSnap.data().officeLocation) {
          setCompanyLocation(docSnap.data().officeLocation);
        } else {
          console.warn('Company location not found in settings/companyProfile');
        }
      } catch (err) {
        console.error('Error fetching company location:', err);
      } finally {
        setLoadingLocation(false);
      }
    };
    fetchCompanyLocation();
  }, []);

  // --- Memoized Calculations ---
  const employeeMap = useMemo(() => {
    const map = new Map();
    employees.forEach((emp) => map.set(emp.id, emp.name));
    return map;
  }, [employees]);

  const todayEvents = useMemo(() => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();
    let combinedEvents = [];

    employees.forEach((emp) => {
      const dob = parseDateSafely(emp.dateOfBirth);
      if (dob && dob.getMonth() === currentMonth && dob.getDate() === currentDay) {
        combinedEvents.push({
          id: `birthday-${emp.id}`,
          message: t.birthday(emp.name),
          type: 'birthday',
        });
      }
    });

    customEvents.forEach((event) => {
      combinedEvents.push({
        id: event.id,
        message: `📌 ${event.title}${event.description ? `: ${event.description}` : ''}`,
        type: event.eventType || 'custom',
      });
    });

    combinedEvents.sort((a, b) => {
      if (a.type === 'birthday' && b.type !== 'birthday') return -1;
      if (a.type !== 'birthday' && b.type === 'birthday') return 1;
      if (a.type === 'popup' && b.type !== 'popup') return -1;
      if (a.type !== 'popup' && b.type === 'popup') return 1;
      return 0;
    });
    return combinedEvents;
  }, [employees, customEvents]);

  useEffect(() => {
    if (!loadingEvents && todayEvents.length > 0) {
      const popupShown = sessionStorage.getItem('eventPopupShownThisSession');
      if (!popupShown) {
        setIsEventPopupOpen(true);
        sessionStorage.setItem('eventPopupShownThisSession', 'true');
      }
    }
  }, [todayEvents, loadingEvents]);

  const achievementDateRange = useMemo(() => {
    const today = new Date();
    let start = new Date();
    let end = new Date();
    end.setHours(23, 59, 59, 999);

    switch (achievementsDateFilter) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case 'yesterday':
        start = new Date(today);
        start.setDate(today.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setDate(today.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        break;
      case 'last7':
        start = new Date(today);
        start.setDate(today.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        break;
      case 'last28':
        start = new Date(today);
        start.setDate(today.getDate() - 27);
        start.setHours(0, 0, 0, 0);
        break;
      case 'custom':
        if (customAchievementStartDate && customAchievementEndDate) {
          start = parseDateSafely(customAchievementStartDate + 'T00:00:00Z');
          end = parseDateSafely(customAchievementEndDate + 'T23:59:59.999Z');
          if (!start || !end || end < start) {
            return { start: null, end: null, isValid: false, filterType: 'custom' };
          }
        } else {
          return { start: null, end: null, isValid: false, filterType: 'custom' };
        }
        break;
      case 'maximum':
      default:
        return { start: null, end: null, isValid: true, filterType: 'maximum' };
    }
    return { start, end, isValid: true, filterType: achievementsDateFilter };
  }, [achievementsDateFilter, customAchievementStartDate, customAchievementEndDate]);

  const userAchievements = useMemo(() => {
    const defaults = {
      created: 0,
      assignedAndCompleted: 0,
      companyApproved: 0,
      customerApproved: 0,
      assignedAndLate: 0,
      currentlyAssigned: 0,
      avgAckTime: null,
      avgCompletionTime: null,
      ackTasksCount: 0,
      completedTasksAfterAckCount: 0,
    };
    if (
      !currentUser ||
      !tasks ||
      tasks.length === 0 ||
      (achievementsDateFilter === 'custom' && !achievementDateRange.isValid)
    ) {
      return defaults;
    }

    const { start: rangeStart, end: rangeEnd, filterType } = achievementDateRange;

    let created = 0;
    let assignedAndCompleted = 0;
    let companyApprovedByMe = 0;
    let customerApprovedByMe = 0;
    let assignedAndLate = 0;
    let currentlyAssignedToMe = 0;

    let totalAckTime = 0;
    let ackCount = 0;
    let totalCompletionTime = 0;
    let completionCount = 0;

    tasks.forEach((task) => {
      const isAssignedToCurrentUser = Array.isArray(task.assignedTo) && task.assignedTo.includes(currentUser.uid);
      const createdAtDate = parseDateSafely(task.createdAt);

      if (task.createdBy === currentUser.uid && isDateInRange(createdAtDate, rangeStart, rangeEnd)) {
        created++;
      }

      if (isAssignedToCurrentUser) {
        if (task.status !== 'Completed' && task.status !== 'Cancelled') {
          if (filterType === 'maximum' || isDateInRange(createdAtDate, rangeStart, rangeEnd)) {
            currentlyAssignedToMe++;
          }
        }

        const ackTimestampObject = task.acknowledgedBy && task.acknowledgedBy[currentUser.uid];
        const ackDate = parseDateSafely(ackTimestampObject);

        if (createdAtDate && ackDate && isDateInRange(ackDate, rangeStart, rangeEnd)) {
          const diff = ackDate.getTime() - createdAtDate.getTime();
          if (diff >= 0) {
            totalAckTime += diff;
            ackCount++;
          }
        }

        const completedDate = parseDateSafely(task.completedAt);
        if (ackDate && completedDate && task.status === 'Completed' && isDateInRange(completedDate, rangeStart, rangeEnd)) {
          const diff = completedDate.getTime() - ackDate.getTime();
          if (diff >= 0) {
            totalCompletionTime += diff;
            completionCount++;
          }
        }

        if (task.status === 'Completed' && isDateInRange(parseDateSafely(task.completedAt), rangeStart, rangeEnd)) {
          assignedAndCompleted++;
        }

        const taskEndDate = parseDateSafely(task.endDate);
        if (isTaskOverdue(task)) {
          if (filterType === 'maximum' || isDateInRange(taskEndDate, rangeStart, rangeEnd)) {
            assignedAndLate++;
          }
        }
      }

      if (task.companyApprovedBy === currentUser.uid && isDateInRange(parseDateSafely(task.companyApprovedAt), rangeStart, rangeEnd)) {
        companyApprovedByMe++;
      }
      if (task.customerApprovedBy === currentUser.uid && isDateInRange(parseDateSafely(task.customerApprovedAt), rangeStart, rangeEnd)) {
        customerApprovedByMe++;
      }
    });

    const avgAckTimeMillis = ackCount > 0 ? totalAckTime / ackCount : null;
    const avgCompletionTimeMillis = completionCount > 0 ? totalCompletionTime / completionCount : null;

    return {
      created,
      assignedAndCompleted,
      companyApproved: companyApprovedByMe,
      customerApproved: customerApprovedByMe,
      assignedAndLate,
      currentlyAssigned: currentlyAssignedToMe,
      avgAckTime: avgAckTimeMillis,
      avgCompletionTime: avgCompletionTimeMillis,
      ackTasksCount: ackCount,
      completedTasksAfterAckCount: completionCount,
    };
  }, [tasks, currentUser, achievementDateRange, achievementsDateFilter]);

  const processedTasks = useMemo(() => {
    const startOfToday = getStartOfToday();
    const endOfToday = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000 - 1);

    let tasksToProcess = tasks.filter((task) => {
      const applyAssigneeFilter =
        (userRole === 'Manager' || userRole === 'Accounts Manager') || selectedAssignee !== 'all';
      if (applyAssigneeFilter && selectedAssignee !== 'all') {
        if (!Array.isArray(task.assignedTo) || !task.assignedTo.includes(selectedAssignee)) return false;
      }

      const statusLower = task.status?.toLowerCase();
      const completedDate = parseDateSafely(task.completedAt);
      const cancelledDate = parseDateSafely(task.cancelledAt);

      if (selectedStatus === 'all') {
        return statusLower !== 'completed' && statusLower !== 'cancelled';
      } else if (selectedStatus === 'Completed') {
        if (statusLower !== 'completed') return false;
        if (completedFilter === 'today') {
          return completedDate && completedDate >= startOfToday && completedDate <= endOfToday;
        }
        return true;
      } else if (selectedStatus === 'Cancelled') {
        if (statusLower !== 'cancelled') return false;
        if (cancelledFilter === 'today') {
          return cancelledDate && cancelledDate >= startOfToday && cancelledDate <= endOfToday;
        }
        return true;
      } else {
        return task.status === selectedStatus;
      }
    });

    tasksToProcess.sort((a, b) => {
      const statusA = a.status?.toLowerCase();
      const statusB = b.status?.toLowerCase();
      const isFinalA = statusA === 'completed' || statusA === 'cancelled';
      const isFinalB = statusB === 'completed' || statusB === 'cancelled';

      if (isFinalA !== isFinalB) return isFinalA ? 1 : -1;

      if (!isFinalA) {
        const overdueA = isTaskOverdue(a);
        const overdueB = isTaskOverdue(b);
        if (overdueA !== overdueB) return overdueA ? -1 : 1;
      }

      const priorityA = getPriorityValue(a.priority);
      const priorityB = getPriorityValue(b.priority);
      if (priorityA !== priorityB) return priorityA - priorityB;

      if (sortField) {
        const directionMultiplier = sortDirection === 'asc' ? 1 : -1;
        let valA, valB;

        switch (sortField) {
          case 'taskName':
            valA = a.taskName?.toLowerCase() || '';
            valB = b.taskName?.toLowerCase() || '';
            break;
          case 'status':
            valA = a.status?.toLowerCase() || '';
            valB = b.status?.toLowerCase() || '';
            break;
          case 'priority':
            return (getPriorityValue(a.priority) - getPriorityValue(b.priority)) * directionMultiplier;
          case 'assignees':
            valA = a.assignedTo?.[0]
              ? (employeeMap.get(a.assignedTo[0])?.toLowerCase() || 'zzz_unassigned')
              : 'zzz_unassigned';
            valB = b.assignedTo?.[0]
              ? (employeeMap.get(b.assignedTo[0])?.toLowerCase() || 'zzz_unassigned')
              : 'zzz_unassigned';
            break;
          case 'endDate':
            valA = parseDateSafely(a.endDate)?.getTime() ?? (sortDirection === 'asc' ? Infinity : -Infinity);
            valB = parseDateSafely(b.endDate)?.getTime() ?? (sortDirection === 'asc' ? Infinity : -Infinity);
            return (valA - valB) * directionMultiplier;
          case 'completedAt':
            valA = parseDateSafely(a.completedAt)?.getTime() ?? (sortDirection === 'asc' ? Infinity : -Infinity);
            valB = parseDateSafely(b.completedAt)?.getTime() ?? (sortDirection === 'asc' ? Infinity : -Infinity);
            return (valA - valB) * directionMultiplier;
          case 'cancelledAt':
            valA = parseDateSafely(a.cancelledAt)?.getTime() ?? (sortDirection === 'asc' ? Infinity : -Infinity);
            valB = parseDateSafely(b.cancelledAt)?.getTime() ?? (sortDirection === 'asc' ? Infinity : -Infinity);
            return (valA - valB) * directionMultiplier;
          default:
            return 0;
        }

        if (typeof valA === 'string' && typeof valB === 'string') {
          return valA.localeCompare(valB) * directionMultiplier;
        } else if (typeof valA === 'number' && typeof valB === 'number') {
          return (valA - valB) * directionMultiplier;
        } else {
          if (valA < valB) return -1 * directionMultiplier;
          if (valA > valB) return 1 * directionMultiplier;
        }
      }
      return 0;
    });

    return tasksToProcess;
  }, [
    tasks,
    selectedStatus,
    selectedAssignee,
    userRole,
    sortField,
    sortDirection,
    employeeMap,
    completedFilter,
    cancelledFilter,
  ]);

  // --- Handlers ---
  const handleSort = useCallback(
    (field) => {
      setSortDirection((prevDirection) =>
        sortField === field ? (prevDirection === 'asc' ? 'desc' : 'asc') : 'asc'
      );
      setSortField(field);
    },
    [sortField]
  );

  const clearFiltersAndSort = useCallback(() => {
    setSelectedStatus('all');
    setSelectedAssignee('all');
    setCompletedFilter('today');
    setCancelledFilter('today');
    setSortField('endDate');
    setSortDirection('asc');
    setAchievementsDateFilter('today');
    setCustomAchievementStartDate('');
    setCustomAchievementEndDate('');
  }, []);

  const statusOptions = useMemo(
    () => ['Not Started', 'In Progress', 'Company Approval', 'Customer Approval', 'Completed', 'Cancelled'],
    []
  );

  const todayForDateInput = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  return (
    <div className={styles.dashboardContainer}>
      <EventPopupModal
        isOpen={isEventPopupOpen}
        onClose={() => setIsEventPopupOpen(false)}
        events={todayEvents}
      />

      {/* One-time announcement popup (new features + attendance rules) for all users */}
      <AnnouncementModal />

      {/* Device push-notification opt-in (FCM) */}
      <PushNotificationPrompt />

      {/* ✅ 24h alert: counted absent because of a missed checkout — lets the user request the manager */}
      <MissedCheckoutAlert />

      {/* ✅ Vacation banner — if on vacation, don't prompt for log/attendance */}
      {!checkingVacation && onVacationTodayFlag && (
        <div className={styles.infoBanner}>
          {t.vacationPre} <strong>{t.vacationWord}</strong> {t.vacationPost}
        </div>
      )}

      {/* ✅ Location permission prompt (one time per session, suppressed on vacation) */}
      {!checkingVacation && !onVacationTodayFlag && !locationPermissionHandled && (
        <LocationPermission onPermissionGiven={handlePermissionResult} />
      )}

      {/* ✅ Attendance (only when office location is available, not on vacation) */}
      {!checkingVacation && !onVacationTodayFlag && !loadingLocation && companyLocation && (
        <AttendanceCard companyLocation={companyLocation} />
      )}

      {/* Events Section */}
      {(todayEvents.length > 0 || loadingEvents) && (
        <div className={styles.eventsSection}>
          <h3 className={styles.eventsTitle}>{t.todaysEvents}</h3>
          {loadingEvents ? (
            <div className={styles.loadingIndicatorSmall}>{t.loadingEvents}</div>
          ) : todayEvents.length > 0 ? (
            <ul className={styles.eventsList}>
              {todayEvents.map((event) => (
                <li
                  key={event.id}
                  className={`${styles.eventItem} ${styles[`eventType_${event.type}`] || ''}`}
                >
                  {event.message}
                  {event.type === 'popup' && (
                    <span className={styles.popupIndicator}>{t.popupIndicator}</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.noEventsMessage}>{t.noEvents}</p>
          )}
        </div>
      )}

      {/* Achievements Section - Hidden for Trainees */}
      {currentUser && userRole !== 'Trainee' && (
        <div className={styles.achievementsSection}>
          <div className={styles.achievementsHeader}>
            <h3 className={styles.achievementsTitle}>{t.quickStats}</h3>
            <div className={styles.achievementsDateFilters}>
              <label htmlFor="achievements-date-filter" className={styles.dateFilterLabel}>
                {t.forLabel}
              </label>
              <select
                id="achievements-date-filter"
                value={achievementsDateFilter}
                onChange={(e) => setAchievementsDateFilter(e.target.value)}
                className={styles.filterSelectSmall}
              >
                <option value="today">{t.optToday}</option>
                <option value="yesterday">{t.optYesterday}</option>
                <option value="last7">{t.optLast7}</option>
                <option value="last28">{t.optLast28}</option>
                <option value="custom">{t.optCustom}</option>
                <option value="maximum">{t.optAllTime}</option>
              </select>
              {achievementsDateFilter === 'custom' && (
                <div className={styles.customDateRangePicker}>
                  <input
                    type="date"
                    aria-label={t.ariaCustomStart}
                    value={customAchievementStartDate}
                    onChange={(e) => setCustomAchievementStartDate(e.target.value)}
                    className={styles.dateInputSmall}
                    max={customAchievementEndDate || todayForDateInput}
                  />
                  <span>{t.toWord}</span>
                  <input
                    type="date"
                    aria-label={t.ariaCustomEnd}
                    value={customAchievementEndDate}
                    onChange={(e) => setCustomAchievementEndDate(e.target.value)}
                    className={styles.dateInputSmall}
                    min={customAchievementStartDate || undefined}
                    max={todayForDateInput}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={styles.achievementsGrid}>
            <div className={styles.achievementCard}>
              <h4>{t.cardCreated}</h4>
              <p className={styles.achievementValue}>{userAchievements.created}</p>
            </div>
            <div className={styles.achievementCard}>
              <h4>{t.cardCurrentlyAssigned}</h4>
              <p className={styles.achievementValue}>{userAchievements.currentlyAssigned}</p>
            </div>
            <div className={styles.achievementCard}>
              <h4>{t.cardAssignedCompleted}</h4>
              <p className={styles.achievementValue}>{userAchievements.assignedAndCompleted}</p>
            </div>
            <div className={styles.achievementCard}>
              <h4>{t.cardAssignedLate}</h4>
              <p
                className={`${styles.achievementValue} ${
                  userAchievements.assignedAndLate > 0 ? styles.lateCount : ''
                }`}
              >
                {userAchievements.assignedAndLate}
              </p>
            </div>
            <div className={styles.achievementCard}>
              <h4>{t.cardAvgAck}</h4>
              <p className={styles.achievementValue}>
                {formatAverageDuration(userAchievements.avgAckTime, language)}
              </p>
              {userAchievements.ackTasksCount > 0 && (
                <small>({userAchievements.ackTasksCount} {t.tasksWord})</small>
              )}
            </div>
            <div className={styles.achievementCard}>
              <h4>
                {t.cardAvgCompletion} <small>{t.fromAck}</small>
              </h4>
              <p className={styles.achievementValue}>
                {formatAverageDuration(userAchievements.avgCompletionTime, language)}
              </p>
              {userAchievements.completedTasksAfterAckCount > 0 && (
                <small>({userAchievements.completedTasksAfterAckCount} {t.tasksWord})</small>
              )}
            </div>

            {(userRole === 'Manager' || userRole === 'Accounts Manager' || userRole === 'Sales') && (
              <>
                <div className={styles.achievementCard}>
                  <h4>{t.cardCompanyApprovals}</h4>
                  <p className={styles.achievementValue}>{userAchievements.companyApproved}</p>
                </div>
                <div className={styles.achievementCard}>
                  <h4>{t.cardCustomerApprovals}</h4>
                  <p className={styles.achievementValue}>{userAchievements.customerApproved}</p>
                </div>
              </>
            )}
          </div>

          {achievementsDateFilter === 'custom' &&
            !achievementDateRange.isValid &&
            (!customAchievementStartDate || !customAchievementEndDate) && (
              <p className={styles.customDateInfo}>
                {t.customDateInfo}
              </p>
            )}
          {achievementsDateFilter === 'custom' &&
            !achievementDateRange.isValid &&
            customAchievementStartDate &&
            customAchievementEndDate && (
              <p className={styles.customDateError}>
                {t.customDateError}
              </p>
            )}
        </div>
      )}

      {/* Header for task list section */}
      <div className={styles.header}>
        <h2>
          {userRole === 'Trainee'
            ? t.titleMyDashboard
            : userRole === 'Manager' || userRole === 'Accounts Manager'
            ? t.titleAllTasks
            : t.titleMyTasks}
        </h2>
        <div className={styles.headerActions}>
          {(userRole === 'Manager' || userRole === 'Accounts Manager') && (
            <Link to="/events/new" className={`${styles.headerButton} ${styles.eventButton}`}>
              {t.addEvent}
            </Link>
          )}
          {(userRole === 'Manager' || userRole === 'Accounts Manager') && (
            <Link to="/task/new" className={`${styles.headerButton} ${styles.createButton}`}>
              {t.createTask}
            </Link>
          )}
          {userRole === 'Trainee' && (
            <Link to="/trainee-tasks" className={`${styles.headerButton} ${styles.viewMyTasksButton}`}>
              {t.viewMyTraineeTasks}
            </Link>
          )}
        </div>
      </div>

      {/* Filters and Task List - Hidden for Trainees */}
      {userRole !== 'Trainee' && (
        <>
          <div className={styles.filtersContainer}>
            <div className={styles.filterGroup}>
              <label htmlFor="statusFilter">{t.statusFilterLabel}</label>
              <select
                id="statusFilter"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className={styles.filterSelect}
                disabled={isLoading}
              >
                <option value="all">{t.allActiveTasks}</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {statusLabel(status, language)}
                  </option>
                ))}
              </select>
            </div>

            {selectedStatus === 'Completed' && (
              <div className={styles.filterGroup}>
                <label htmlFor="completedFilter">{t.showCompleted}</label>
                <select
                  id="completedFilter"
                  value={completedFilter}
                  onChange={(e) => setCompletedFilter(e.target.value)}
                  className={styles.filterSelect}
                  disabled={isLoading}
                >
                  <option value="today">{t.completedToday}</option>
                  <option value="all">{t.allTime}</option>
                </select>
              </div>
            )}

            {selectedStatus === 'Cancelled' && (
              <div className={styles.filterGroup}>
                <label htmlFor="cancelledFilter">{t.showCancelled}</label>
                <select
                  id="cancelledFilter"
                  value={cancelledFilter}
                  onChange={(e) => setCancelledFilter(e.target.value)}
                  className={styles.filterSelect}
                  disabled={isLoading}
                >
                  <option value="today">{t.cancelledToday}</option>
                  <option value="all">{t.allTime}</option>
                </select>
              </div>
            )}

            {(userRole === 'Manager' || userRole === 'Accounts Manager') && (
              <div className={styles.filterGroup}>
                <label htmlFor="assigneeFilter">{t.assigneeLabel}</label>
                <select
                  id="assigneeFilter"
                  value={selectedAssignee}
                  onChange={(e) => setSelectedAssignee(e.target.value)}
                  className={styles.filterSelect}
                  disabled={isLoading || employees.length === 0}
                >
                  <option value="all">{t.allAssignees}</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <button
              onClick={clearFiltersAndSort}
              className={styles.clearButton}
              disabled={
                isLoading ||
                (selectedStatus === 'all' &&
                  selectedAssignee === 'all' &&
                  completedFilter === 'today' &&
                  cancelledFilter === 'today' &&
                  sortField === 'endDate' &&
                  sortDirection === 'asc' &&
                  achievementsDateFilter === 'today')
              }
            >
              {t.clearFilters}
            </button>
          </div>

          <div className={styles.taskListContainer}>
            {loadingTasks ? (
              <div className={styles.loadingIndicator}>{t.loadingTasks}</div>
            ) : error && !tasks.length ? (
              <div className={styles.errorMessage}>{error}</div>
            ) : (
              <>
                <TaskList
                  tasks={processedTasks}
                  employees={employees}
                  employeeMap={employeeMap}
                  currentUserId={currentUser?.uid}
                  currentSort={{ field: sortField, direction: sortDirection }}
                  onSort={handleSort}
                />
                {processedTasks.length === 0 && !loadingTasks && !error && (
                  <p className={styles.noTasksMessage}>{t.noTasks}</p>
                )}
                {error && tasks.length > 0 && (
                  <p className={styles.errorMessageSmall}>{error}</p>
                )}
              </>
            )}
          </div>
        </>
      )}

      {userRole === 'Trainee' && (
        <div className={styles.traineeInfoMessage}>
          <p>
            {t.traineeInfoPre}
            <Link to="/trainee-tasks">{t.traineeDashLink}</Link>{t.traineeInfoPost}
          </p>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
